import { c as _c } from "react/compiler-runtime";
import { FaroTableBulkActions } from "@components/common/faro-table/faro-table-bulk-actions";
import { MapListTwoToneIcon } from "@components/common/icons";
import { VirtualizedGrid } from "@components/virtualized/virtualized-grid";
import { ArrowDownIcon, Text } from "@flat-ui";
import { ButtonBase } from "@mui-wrapper";
import { ProjectsMapInfoPage } from "@pages/projects/projects-map/projects-map-info-page";
import { ProjectsMapListItem } from "@pages/projects/projects-map/projects-map-list-item";
import { ProjectsMapSkeleton } from "@pages/projects/projects-map/projects-map-skeleton";
import { useProjectsMapBulkActions } from "@pages/projects/projects-map/use-projects-map-bulk-actions";
import { useCallback, useMemo, useState } from "react";
/** Estimated height in pixels of a list item, used as the initial virtualization estimate */
const LIST_ITEM_HEIGHT = 132;

/** Label for the section listing the projects currently visible on the map */
const VISIBLE_SECTION_LABEL = "Visible on map";

/** Label for the section listing the projects not visible on the map */
const OTHER_SECTION_LABEL = "Other projects";

/**
 * Label used instead of {@link OTHER_SECTION_LABEL} while no project can appear on the
 * map: "Other" has no referent when there is no "Visible on map" section above it.
 */
const ALL_SECTION_LABEL = "All projects";

/** Identifies each collapsible section of the list */

/** A row rendered in the virtualized list: either a section header or a project. */

/**
 * @returns A clickable header that toggles the collapsed state of a project section.
 */
function SectionHeader(t0) {
  const $ = _c(13);
  const {
    label,
    count,
    isCollapsed,
    onToggle
  } = t0;
  const t1 = !isCollapsed;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: 0.5,
      width: "100%",
      pt: 1,
      pb: 0.5,
      px: "8px"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = isCollapsed ? "rotate(-90deg)" : "rotate(0deg)";
  let t4;
  if ($[1] !== t3) {
    t4 = <ArrowDownIcon sx={{
      width: "1rem",
      height: "1rem",
      color: "text.secondary",
      transform: t3,
      transition: "transform 0.2s ease-in-out"
    }} />;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== count) {
    t5 = <var>{count}</var>;
    $[3] = count;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== label || $[6] !== t5) {
    t6 = <Text variant="labelM" color="text.secondary">{label} ({t5})</Text>;
    $[5] = label;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== onToggle || $[9] !== t1 || $[10] !== t4 || $[11] !== t6) {
    t7 = <ButtonBase onClick={onToggle} aria-expanded={t1} sx={t2}>{t4}{t6}</ButtonBase>;
    $[8] = onToggle;
    $[9] = t1;
    $[10] = t4;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}

/**
 * @returns A virtualized list that groups projects into a collapsible "visible on the map"
 * section and a collapsible "other projects" section, each sorted by the same active sort criteria.
 */
export function ProjectsMapList(t0) {
  const $ = _c(39);
  const {
    companyId,
    visibleProjects,
    otherProjects,
    projectArchivingState,
    goTo,
    onHoverChange,
    highlightedProjectIds,
    hasSearchQuery,
    hasNoProjectsWithPosition,
    sortBy
  } = t0;
  const [collapsedSections, setCollapsedSections] = useState(_temp);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = section => {
      setCollapsedSections(prev => {
        const next = new Set(prev);
        if (next.has(section)) {
          next.delete(section);
        } else {
          next.add(section);
        }
        return next;
      });
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const toggleSection = t1;
  let t2;
  if ($[1] !== otherProjects || $[2] !== visibleProjects) {
    t2 = [...visibleProjects, ...otherProjects];
    $[1] = otherProjects;
    $[2] = visibleProjects;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const projects = t2;
  let result;
  if ($[4] !== collapsedSections || $[5] !== hasNoProjectsWithPosition || $[6] !== otherProjects || $[7] !== visibleProjects) {
    result = [];
    if (visibleProjects.length > 0) {
      let t3;
      if ($[9] !== visibleProjects.length) {
        t3 = {
          kind: "header",
          key: "header-visible",
          section: "visible",
          label: VISIBLE_SECTION_LABEL,
          count: visibleProjects.length
        };
        $[9] = visibleProjects.length;
        $[10] = t3;
      } else {
        t3 = $[10];
      }
      result.push(t3);
      if (!collapsedSections.has("visible")) {
        for (const project of visibleProjects) {
          result.push({
            kind: "project",
            key: project.id,
            project
          });
        }
      }
    }
    if (otherProjects.length > 0) {
      const t3 = hasNoProjectsWithPosition ? ALL_SECTION_LABEL : OTHER_SECTION_LABEL;
      let t4;
      if ($[11] !== otherProjects.length || $[12] !== t3) {
        t4 = {
          kind: "header",
          key: "header-other",
          section: "other",
          label: t3,
          count: otherProjects.length
        };
        $[11] = otherProjects.length;
        $[12] = t3;
        $[13] = t4;
      } else {
        t4 = $[13];
      }
      result.push(t4);
      if (!collapsedSections.has("other")) {
        for (const project_0 of otherProjects) {
          result.push({
            kind: "project",
            key: project_0.id,
            project: project_0
          });
        }
      }
    }
    $[4] = collapsedSections;
    $[5] = hasNoProjectsWithPosition;
    $[6] = otherProjects;
    $[7] = visibleProjects;
    $[8] = result;
  } else {
    result = $[8];
  }
  const rows = result;
  let t3;
  if ($[14] !== companyId || $[15] !== projectArchivingState || $[16] !== projects) {
    t3 = {
      companyId,
      projects,
      projectArchivingState
    };
    $[14] = companyId;
    $[15] = projectArchivingState;
    $[16] = projects;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const {
    bulkActions,
    selectedProjects
  } = useProjectsMapBulkActions(t3);
  if (projects.length === 0) {
    const t4 = hasSearchQuery ? "No matching projects found" : `No ${projectArchivingState} projects`;
    let t5;
    if ($[18] !== t4) {
      t5 = <ProjectsMapInfoPage title={t4} source={MapListTwoToneIcon} />;
      $[18] = t4;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    return t5;
  }
  let t4;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      xs: 1,
      sm: 1,
      md: 1,
      lg: 1,
      xl: 1,
      xxl: 1,
      xxxl: 1
    };
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== collapsedSections || $[22] !== goTo || $[23] !== highlightedProjectIds || $[24] !== onHoverChange || $[25] !== sortBy) {
    t5 = row => {
      if (row.kind === "header") {
        return <SectionHeader key={row.key} label={row.label} count={row.count} isCollapsed={collapsedSections.has(row.section)} onToggle={() => toggleSection(row.section)} />;
      }
      return <ProjectsMapListItem key={row.key} project={row.project} goTo={goTo} onHoverChange={onHoverChange} isHighlighted={highlightedProjectIds.has(row.project.id)} sortBy={sortBy} />;
    };
    $[21] = collapsedSections;
    $[22] = goTo;
    $[23] = highlightedProjectIds;
    $[24] = onHoverChange;
    $[25] = sortBy;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== rows || $[28] !== t5) {
    t6 = <VirtualizedGrid items={rows} isLoading={false} estimateSize={LIST_ITEM_HEIGHT} shouldMeasureRowHeight={true} columnConfig={t4} renderSkeletonItem={_temp2} renderItem={t5} />;
    $[27] = rows;
    $[28] = t5;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  const t7 = selectedProjects.length;
  let t8;
  if ($[30] !== projects) {
    t8 = projects.map(_temp3);
    $[30] = projects;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== bulkActions || $[33] !== selectedProjects.length || $[34] !== t8) {
    t9 = <FaroTableBulkActions bulkActionsButtons={bulkActions} numberOfSelectedRows={t7} isLoading={false} allRowIds={t8} isSelectAllAvailable={true} />;
    $[32] = bulkActions;
    $[33] = selectedProjects.length;
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== t6 || $[37] !== t9) {
    t10 = <>{t6}{t9}</>;
    $[36] = t6;
    $[37] = t9;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  return t10;
}
function _temp3(project_1) {
  return project_1.id;
}
function _temp2(index) {
  return <ProjectsMapSkeleton key={index} />;
}
function _temp() {
  return new Set();
}